<template>
  <div class="resizable-text-area-wrapper">
    <textarea
      ref="textarea"
      :dir="isRTLView ? 'rtl' : 'ltr'"
      :placeholder="placeholder"
      :value="value"
      @input="onInput"
      @focus="onFocus"
      @keyup="onKeyup"
      @blur="onBlur"
    />
    <div v-if="hasToolbar" class="resizable-text-area__toolbar">
      <div class="toolbar-left">
        <div class="relative flex ltr:mr-[10px] rtl:ml-[10px]">
          <span
            ref="emojiTrigger"
            v-tooltip.top="$t('CONVERSATION.REPLYBOX.TIP_EMOJI_ICON')"
            :title="$t('CONVERSATION.REPLYBOX.TIP_EMOJI_ICON')"
            class="material-symbols-outlined message-toolbar-item"
            @click.stop="toggleEmojiPicker"
          >
            sentiment_satisfied
          </span>
          <emoji-input
            v-show="showEmojiPicker"
            v-on-clickaway="onClickAwayEmojiPicker"
            :class="emojiDialogClassOnRTLView"
            :on-click="emojiOnClick"
            :trigger-el="$refs.emojiTrigger"
            :visible="showEmojiPicker"
          />
        </div>
        <slot name="toolbar-left" />
      </div>
      <div class="toolbar-right">
        <slot name="toolbar-right" />
      </div>
    </div>
  </div>
</template>

<script>
import {
  appendSignature,
  removeSignature,
  extractTextFromMarkdown,
} from 'dashboard/helper/editorHelper';
import { mixin as clickaway } from 'vue-clickaway';
import rtlMixin from 'shared/mixins/rtlMixin';

const EmojiInput = () => import('shared/components/emoji/EmojiInput');

const TYPING_INDICATOR_IDLE_TIME = 500;
export default {
  components: { EmojiInput },
  mixins: [clickaway, rtlMixin],
  props: {
    placeholder: {
      type: String,
      default: '',
    },
    value: {
      type: String,
      default: '',
    },
    minHeight: {
      type: Number,
      default: 2,
    },
    signature: {
      type: String,
      default: '',
    },
    // add this as a prop, so that we won't have to include uiSettingsMixin
    sendWithSignature: {
      type: Boolean,
      default: false,
    },
    // allowSignature is a kill switch, ensuring no signature methods are triggered except when this flag is true
    allowSignature: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      idleTimer: null,
      showEmojiPicker: false,
    };
  },
  computed: {
    cleanedSignature() {
      // clean the signature, this will ensure that we don't have
      // any markdown formatted text in the signature
      return extractTextFromMarkdown(this.signature);
    },
    hasToolbar() {
      return !!(this.$slots['toolbar-left'] || this.$slots['toolbar-right']);
    },
    emojiDialogClassOnRTLView() {
      return this.isRTLView ? 'emoji-dialog--rtl' : '';
    },
  },
  watch: {
    value() {
      this.resizeTextarea();
      // 🚨 watch triggers every time the value is changed, we cannot set this to focus then
      // when this runs, it sets the cursor to the end of the body, ignoring the signature
      // Suppose if someone manually set the cursor to the middle of the body
      // and starts typing, the cursor will be set to the end of the body
      // A surprise cursor jump? Definitely not user-friendly.
      if (document.activeElement !== this.$refs.textarea) {
        this.$nextTick(() => {
          this.setCursor();
        });
      }
    },
    sendWithSignature(newValue) {
      if (this.allowSignature) {
        this.toggleSignatureInEditor(newValue);
      }
    },
  },
  mounted() {
    this.$nextTick(() => {
      if (this.value) {
        this.resizeTextarea();
        this.setCursor();
      } else {
        this.focus();
      }
    });
  },
  methods: {
    toggleEmojiPicker() {
      this.showEmojiPicker = !this.showEmojiPicker;
    },
    onClickAwayEmojiPicker() {
      if (this.showEmojiPicker) this.hideEmojiPicker();
    },
    hideEmojiPicker() {
      this.showEmojiPicker = false;
    },
    emojiOnClick(emoji) {
      const textarea = this.$refs.textarea;
      if (!textarea) return;

      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const newValue =
        this.value.slice(0, start) + emoji + this.value.slice(end);

      this.$emit('input', newValue);
      this.hideEmojiPicker();

      this.$nextTick(() => {
        textarea.focus();
        const cursorPos = start + emoji.length;
        textarea.setSelectionRange(cursorPos, cursorPos);
      });
    },
    resizeTextarea() {
      const textarea = this.$refs.textarea;
      if (!textarea) return;
      if (!this.value) {
        textarea.style.height = `${this.minHeight}rem`;
      } else {
        textarea.style.height = `${textarea.scrollHeight}px`;
      }
    },
    // The toggleSignatureInEditor gets the new value from the
    // watcher, this means that if the value is true, the signature
    // is supposed to be added, else we remove it.
    toggleSignatureInEditor(signatureEnabled) {
      const valueWithSignature = signatureEnabled
        ? appendSignature(this.value, this.cleanedSignature)
        : removeSignature(this.value, this.cleanedSignature);

      this.$emit('input', valueWithSignature);

      this.$nextTick(() => {
        this.resizeTextarea();
        this.setCursor();
      });
    },
    setCursor() {
      const bodyWithoutSignature = removeSignature(
        this.value,
        this.cleanedSignature
      );

      // only trim at end, so if there are spaces at the start, those are not removed
      const bodyEndsAt = bodyWithoutSignature.trimEnd().length;
      const textarea = this.$refs.textarea;

      if (textarea) {
        textarea.focus();
        textarea.setSelectionRange(bodyEndsAt, bodyEndsAt);
      }
    },
    onInput(event) {
      this.$emit('input', event.target.value);
      this.resizeTextarea();
    },
    resetTyping() {
      this.$emit('typing-off');
      this.idleTimer = null;
    },
    turnOffIdleTimer() {
      if (this.idleTimer) {
        clearTimeout(this.idleTimer);
      }
    },
    onKeyup() {
      if (!this.idleTimer) {
        this.$emit('typing-on');
      }
      this.turnOffIdleTimer();
      this.idleTimer = setTimeout(
        () => this.resetTyping(),
        TYPING_INDICATOR_IDLE_TIME
      );
    },
    onBlur() {
      this.turnOffIdleTimer();
      this.resetTyping();
      this.$emit('blur');
    },
    onFocus() {
      this.$emit('focus');
    },
    focus() {
      if (this.$refs.textarea) this.$refs.textarea.focus();
    },
  },
};
</script>

<style lang="scss" scoped>
.resizable-text-area-wrapper {
  display: flex;
  flex-direction: column;
  width: 100%;

  textarea {
    @apply bg-transparent border-0;
  }
}

.resizable-text-area__toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.toolbar-left {
  display: flex;
  align-items: center;
}

.toolbar-right {
  display: flex;
  align-items: center;
}

.message-toolbar-item {
  @apply w-8 h-8 flex items-center justify-center rounded-md cursor-pointer;
  @apply text-[24px] text-[#6B7280] hover:bg-gray-100 p-1 select-none;

  &.active,
  ~ label:hover {
    @apply bg-gray-100;
  }

  &.disabled {
    color: #dfdfdf;
    cursor: not-allowed;
    user-select: none;
  }
}
</style>
